import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Receipt, ArrowLeft, RefreshCw, Download, Plus, Pencil, Trash2, Check, X,
  FileText, DollarSign, Percent, Calculator, Tag, Filter, Search, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from "recharts";

const CHART_COLORS = [
  "hsl(195, 72%, 36%)",
  "hsl(158, 64%, 40%)",
  "hsl(38, 92%, 50%)",
  "hsl(280, 65%, 60%)",
  "hsl(4, 74%, 55%)",
  "hsl(210, 70%, 50%)",
  "hsl(120, 45%, 45%)",
  "hsl(45, 90%, 55%)"
];

export default function TaxManagementPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // State
  const [activeTab, setActiveTab] = useState("categories");
  const [taxCategories, setTaxCategories] = useState([]);
  const [taxSummary, setTaxSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Dialog state
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    code: "",
    description: "",
    tax_rate: "",
    is_deductible: false,
    deduction_percentage: 100,
    category_type: "both",
    irs_category: ""
  });
  
  // Bulk assignment state
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkTaxCategory, setBulkTaxCategory] = useState("");
  const [bulkDeductible, setBulkDeductible] = useState(false);
  
  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv");
  
  // Transaction filter
  const [txFilter, setTxFilter] = useState("all"); // all, categorized, uncategorized
  const [txSearch, setTxSearch] = useState("");

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [categoriesRes, summaryRes, transactionsRes] = await Promise.all([
        api.get("/tax-categories?include_inactive=true"),
        api.get(`/reports/tax-summary?year=${selectedYear}`),
        api.get("/transactions?limit=500")
      ]);
      setTaxCategories(categoriesRes.data);
      setTaxSummary(summaryRes.data);
      setTransactions(transactionsRes.data || []);
    } catch (error) {
      console.error("Error fetching tax data:", error);
      toast.error("Failed to load tax management data");
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetCategoryForm = () => {
    setCategoryForm({
      name: "",
      code: "",
      description: "",
      tax_rate: "",
      is_deductible: false,
      deduction_percentage: 100,
      category_type: "both",
      irs_category: ""
    });
    setEditingCategory(null);
  };

  const handleCreateCategory = async () => {
    if (!categoryForm.name || !categoryForm.code) {
      toast.error("Name and code are required");
      return;
    }
    
    try {
      const payload = {
        ...categoryForm,
        tax_rate: categoryForm.tax_rate ? parseFloat(categoryForm.tax_rate) : null,
        deduction_percentage: parseFloat(categoryForm.deduction_percentage) || 100
      };
      
      if (editingCategory) {
        await api.put(`/tax-categories/${editingCategory.id}`, payload);
        toast.success("Tax category updated");
      } else {
        await api.post("/tax-categories", payload);
        toast.success("Tax category created");
      }
      
      setShowCategoryDialog(false);
      resetCategoryForm();
      fetchData();
    } catch (error) {
      console.error("Error saving category:", error);
      toast.error(error.response?.data?.detail || "Failed to save tax category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!confirm("Are you sure you want to delete this tax category?")) return;
    
    try {
      const result = await api.delete(`/tax-categories/${categoryId}`);
      if (result.data.deactivated) {
        toast.info("Category deactivated (has associated transactions)");
      } else {
        toast.success("Tax category deleted");
      }
      fetchData();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error("Failed to delete tax category");
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      code: category.code,
      description: category.description || "",
      tax_rate: category.tax_rate?.toString() || "",
      is_deductible: category.is_deductible,
      deduction_percentage: category.deduction_percentage,
      category_type: category.category_type,
      irs_category: category.irs_category || ""
    });
    setShowCategoryDialog(true);
  };

  const handleUpdateTransactionTax = async (transactionId, taxCategoryId, isDeductible) => {
    try {
      await api.put(`/transactions/${transactionId}/tax`, {
        tax_category_id: taxCategoryId || null,
        is_tax_deductible: isDeductible
      });
      toast.success("Transaction updated");
      fetchData();
    } catch (error) {
      console.error("Error updating transaction:", error);
      toast.error("Failed to update transaction");
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedTransactions.length === 0) {
      toast.error("No transactions selected");
      return;
    }
    
    try {
      await api.post("/transactions/bulk-tax", {
        transaction_ids: selectedTransactions,
        tax_category_id: bulkTaxCategory || null,
        is_tax_deductible: bulkDeductible
      });
      toast.success(`Updated ${selectedTransactions.length} transactions`);
      setShowBulkDialog(false);
      setSelectedTransactions([]);
      setBulkTaxCategory("");
      setBulkDeductible(false);
      fetchData();
    } catch (error) {
      console.error("Error bulk updating:", error);
      toast.error("Failed to update transactions");
    }
  };

  const handleExport = async () => {
    try {
      if (exportFormat === "csv") {
        const response = await api.get(`/reports/tax-export?format=csv&year=${selectedYear}`, {
          responseType: "blob"
        });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `tax_export_${selectedYear}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const response = await api.get(`/reports/tax-export?format=json&year=${selectedYear}`);
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `tax_export_${selectedYear}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      toast.success("Export downloaded successfully");
      setShowExportDialog(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(tx => {
    if (txFilter === "categorized" && !tx.tax_category_id) return false;
    if (txFilter === "uncategorized" && tx.tax_category_id) return false;
    if (txSearch && !tx.name?.toLowerCase().includes(txSearch.toLowerCase()) && 
        !tx.description?.toLowerCase().includes(txSearch.toLowerCase())) return false;
    return true;
  });

  // Toggle transaction selection
  const toggleTransaction = (txId) => {
    setSelectedTransactions(prev => 
      prev.includes(txId) ? prev.filter(id => id !== txId) : [...prev, txId]
    );
  };

  const toggleAllTransactions = () => {
    if (selectedTransactions.length === filteredTransactions.length) {
      setSelectedTransactions([]);
    } else {
      setSelectedTransactions(filteredTransactions.map(tx => tx.id));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background" data-testid="tax-management-page">
        <header className="border-b bg-card">
          <div className="max-w-[1400px] mx-auto px-4 py-4">
            <Skeleton className="h-8 w-64" />
          </div>
        </header>
        <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-28" />)}
          </div>
          <Skeleton className="h-[400px]" />
        </main>
      </div>
    );
  }

  // Chart data
  const pieChartData = taxSummary?.by_tax_category?.filter(c => c.total_expenses > 0 || c.total_income > 0).map((cat, idx) => ({
    name: cat.tax_category_name,
    value: cat.total_expenses + cat.total_income,
    fill: CHART_COLORS[idx % CHART_COLORS.length]
  })) || [];

  const barChartData = taxSummary?.by_tax_category?.filter(c => c.total_expenses > 0).slice(0, 8).map(cat => ({
    name: cat.tax_category_code,
    Deductible: cat.deductible_amount,
    "Non-Deductible": cat.total_expenses - cat.deductible_amount
  })) || [];

  return (
    <div className="min-h-screen bg-background" data-testid="tax-management-page">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="max-w-[1400px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/dashboard")}
                data-testid="back-button"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Receipt className="h-6 w-6 text-primary" />
                  Tax Management
                </h1>
                <p className="text-sm text-muted-foreground">
                  Categorize transactions for tax reporting and accountant export
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-[100px]" data-testid="year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                data-testid="refresh-button"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => setShowExportDialog(true)}
                data-testid="export-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="total-income-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-500" />
                Total Income
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {formatCurrency(taxSummary?.total_income)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedYear} tax year
              </p>
            </CardContent>
          </Card>

          <Card data-testid="total-expenses-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-red-500" />
                Total Expenses
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {formatCurrency(taxSummary?.total_expenses)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {taxSummary?.by_tax_category?.length || 0} categories used
              </p>
            </CardContent>
          </Card>

          <Card data-testid="deductible-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Calculator className="h-4 w-4 text-blue-500" />
                Total Deductible
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {formatCurrency(taxSummary?.total_deductible)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {taxSummary?.total_expenses > 0 ? 
                  ((taxSummary.total_deductible / taxSummary.total_expenses) * 100).toFixed(1) : 0}% of expenses
              </p>
            </CardContent>
          </Card>

          <Card data-testid="categorization-card">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-2">
                <Tag className="h-4 w-4" />
                Categorization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {taxSummary?.categorization_percentage?.toFixed(0) || 0}%
              </div>
              <div className="flex items-center gap-2 mt-1">
                <Progress 
                  value={taxSummary?.categorization_percentage || 0} 
                  className="h-2 flex-1"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {taxSummary?.uncategorized_count || 0} uncategorized
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList data-testid="tax-tabs">
            <TabsTrigger value="categories">Tax Categories</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="summary">Summary</TabsTrigger>
          </TabsList>

          {/* Tax Categories Tab */}
          <TabsContent value="categories" className="space-y-4">
            <Card data-testid="tax-categories-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Tax Categories</CardTitle>
                    <CardDescription>Define categories for tax classification</CardDescription>
                  </div>
                  <Button
                    onClick={() => { resetCategoryForm(); setShowCategoryDialog(true); }}
                    data-testid="add-category-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Deductible</TableHead>
                      <TableHead className="text-right">Transactions</TableHead>
                      <TableHead className="text-right">Total Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxCategories.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                          <div className="flex flex-col items-center gap-2">
                            <Receipt className="h-8 w-8 text-muted-foreground/50" />
                            <span>No tax categories yet. Create one to get started.</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      taxCategories.map((category) => (
                        <TableRow key={category.id}>
                          <TableCell className="font-medium">{category.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{category.code}</Badge>
                          </TableCell>
                          <TableCell className="capitalize">{category.category_type}</TableCell>
                          <TableCell>
                            {category.is_deductible ? (
                              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                {category.deduction_percentage}%
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">No</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{category.transaction_count}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(category.total_amount)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={category.is_active ? "default" : "secondary"}>
                              {category.is_active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEditCategory(category)}
                                data-testid={`edit-category-${category.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteCategory(category.id)}
                                data-testid={`delete-category-${category.id}`}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-4">
            <Card data-testid="transactions-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Transaction Tax Assignment</CardTitle>
                    <CardDescription>Assign tax categories to transactions</CardDescription>
                  </div>
                  {selectedTransactions.length > 0 && (
                    <Button
                      onClick={() => setShowBulkDialog(true)}
                      data-testid="bulk-assign-button"
                    >
                      <Tag className="h-4 w-4 mr-2" />
                      Bulk Assign ({selectedTransactions.length})
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Filters */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search transactions..."
                      value={txSearch}
                      onChange={(e) => setTxSearch(e.target.value)}
                      className="pl-9"
                      data-testid="tx-search"
                    />
                  </div>
                  <Select value={txFilter} onValueChange={setTxFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="tx-filter">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Transactions</SelectItem>
                      <SelectItem value="categorized">Categorized</SelectItem>
                      <SelectItem value="uncategorized">Uncategorized</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedTransactions.length === filteredTransactions.length && filteredTransactions.length > 0}
                          onCheckedChange={toggleAllTransactions}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Tax Category</TableHead>
                      <TableHead>Deductible</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTransactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          No transactions found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTransactions.slice(0, 50).map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedTransactions.includes(tx.id)}
                              onCheckedChange={() => toggleTransaction(tx.id)}
                              data-testid={`select-tx-${tx.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(tx.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {tx.name || tx.description || "No description"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={tx.type === "income" ? "default" : "secondary"}>
                              {tx.type}
                            </Badge>
                          </TableCell>
                          <TableCell className={`text-right font-medium ${tx.type === "income" ? "text-green-600" : "text-red-600"}`}>
                            {tx.type === "income" ? "+" : "-"}{formatCurrency(Math.abs(tx.amount))}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={tx.tax_category_id || "none"}
                              onValueChange={(v) => handleUpdateTransactionTax(tx.id, v === "none" ? null : v, tx.is_tax_deductible)}
                            >
                              <SelectTrigger className="w-[140px]" data-testid={`tx-category-${tx.id}`}>
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                {taxCategories.filter(c => c.is_active).map(cat => (
                                  <SelectItem key={cat.id} value={cat.id}>
                                    {cat.code} - {cat.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={tx.is_tax_deductible}
                              onCheckedChange={(checked) => handleUpdateTransactionTax(tx.id, tx.tax_category_id, checked)}
                              data-testid={`tx-deductible-${tx.id}`}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                {filteredTransactions.length > 50 && (
                  <p className="text-sm text-muted-foreground text-center">
                    Showing 50 of {filteredTransactions.length} transactions
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Distribution Pie Chart */}
              <Card data-testid="distribution-chart">
                <CardHeader>
                  <CardTitle>Expense Distribution</CardTitle>
                  <CardDescription>By tax category</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    {pieChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="value"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            labelLine={false}
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        No data to display
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Deductible vs Non-Deductible Bar Chart */}
              <Card data-testid="deductible-chart">
                <CardHeader>
                  <CardTitle>Deductible Analysis</CardTitle>
                  <CardDescription>Deductible vs non-deductible expenses</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    {barChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={barChartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="Deductible" stackId="a" fill="hsl(158, 64%, 40%)" />
                          <Bar dataKey="Non-Deductible" stackId="a" fill="hsl(4, 74%, 55%)" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        No data to display
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Category Summary Table */}
            <Card data-testid="category-summary-table">
              <CardHeader>
                <CardTitle>Category Summary</CardTitle>
                <CardDescription>Tax year {selectedYear}</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead className="text-right">Transactions</TableHead>
                      <TableHead className="text-right">Income</TableHead>
                      <TableHead className="text-right">Expenses</TableHead>
                      <TableHead className="text-right">Deductible</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {taxSummary?.by_tax_category?.map((cat, idx) => (
                      <TableRow key={cat.tax_category_id || "uncategorized"}>
                        <TableCell className="font-medium">{cat.tax_category_name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{cat.tax_category_code}</Badge>
                        </TableCell>
                        <TableCell className="text-right">{cat.transaction_count}</TableCell>
                        <TableCell className="text-right text-green-600">
                          {cat.total_income > 0 ? formatCurrency(cat.total_income) : "-"}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          {cat.total_expenses > 0 ? formatCurrency(cat.total_expenses) : "-"}
                        </TableCell>
                        <TableCell className="text-right text-blue-600">
                          {cat.deductible_amount > 0 ? formatCurrency(cat.deductible_amount) : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-bold bg-muted/50">
                      <TableCell>Total</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right">
                        {(taxSummary?.categorized_count || 0) + (taxSummary?.uncategorized_count || 0)}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {formatCurrency(taxSummary?.total_income)}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {formatCurrency(taxSummary?.total_expenses)}
                      </TableCell>
                      <TableCell className="text-right text-blue-600">
                        {formatCurrency(taxSummary?.total_deductible)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit" : "Add"} Tax Category</DialogTitle>
            <DialogDescription>
              Define a tax category for transaction classification
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cat-name">Name *</Label>
                <Input
                  id="cat-name"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Business Expense"
                  data-testid="cat-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cat-code">Code *</Label>
                <Input
                  id="cat-code"
                  value={categoryForm.code}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., BUS-EXP"
                  maxLength={10}
                  data-testid="cat-code-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-desc">Description</Label>
              <Textarea
                id="cat-desc"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description for this category"
                rows={2}
                data-testid="cat-desc-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cat-type">Type</Label>
                <Select
                  value={categoryForm.category_type}
                  onValueChange={(v) => setCategoryForm(prev => ({ ...prev, category_type: v }))}
                >
                  <SelectTrigger id="cat-type" data-testid="cat-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Both</SelectItem>
                    <SelectItem value="income">Income Only</SelectItem>
                    <SelectItem value="expense">Expense Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cat-irs">IRS Category</Label>
                <Input
                  id="cat-irs"
                  value={categoryForm.irs_category}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, irs_category: e.target.value }))}
                  placeholder="e.g., Schedule C Line 18"
                  data-testid="cat-irs-input"
                />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2">
                <Switch
                  id="cat-deductible"
                  checked={categoryForm.is_deductible}
                  onCheckedChange={(checked) => setCategoryForm(prev => ({ ...prev, is_deductible: checked }))}
                  data-testid="cat-deductible-switch"
                />
                <Label htmlFor="cat-deductible">Tax Deductible</Label>
              </div>
              {categoryForm.is_deductible && (
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={categoryForm.deduction_percentage}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, deduction_percentage: parseFloat(e.target.value) || 100 }))}
                    className="w-20"
                    min={0}
                    max={100}
                    data-testid="cat-deduction-pct-input"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCategory} data-testid="save-category-button">
              {editingCategory ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assignment Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Tax Assignment</DialogTitle>
            <DialogDescription>
              Assign tax category to {selectedTransactions.length} selected transactions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tax Category</Label>
              <Select value={bulkTaxCategory || "clear"} onValueChange={(v) => setBulkTaxCategory(v === "clear" ? "" : v)}>
                <SelectTrigger data-testid="bulk-category-select">
                  <SelectValue placeholder="Select tax category..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clear">Clear Category</SelectItem>
                  {taxCategories.filter(c => c.is_active).map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.code} - {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="bulk-deductible"
                checked={bulkDeductible}
                onCheckedChange={setBulkDeductible}
                data-testid="bulk-deductible-switch"
              />
              <Label htmlFor="bulk-deductible">Mark as Tax Deductible</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkUpdate} data-testid="bulk-assign-confirm">
              Apply to {selectedTransactions.length} Transactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Export Tax Report</DialogTitle>
            <DialogDescription>
              Export transactions with tax categorization for your accountant
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger data-testid="export-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV (Excel/Spreadsheet)</SelectItem>
                  <SelectItem value="json">JSON (Data Format)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <p className="text-sm font-medium">Export includes:</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Date, description, category</li>
                <li>• Tax category and code</li>
                <li>• Amount and type (income/expense)</li>
                <li>• Deductible status and amount</li>
                <li>• Tax notes</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleExport} data-testid="export-confirm">
              <Download className="h-4 w-4 mr-2" />
              Download {exportFormat.toUpperCase()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
